/**
 * DSPM API module - Data Security Posture Management.
 *
 * Real-split (was a stub re-export from ../api). Hosts the canonical
 * implementations of dspmApi, dataResidencyApi, dspmContentApi, and
 * aiTrainingDataApi. Public signatures unchanged.
 */
import { _unwrap } from './_unwrap';
import { apiClient as fetchClient } from './client';
import type { CloudProvider, PaginatedResponse, DataAsset } from '../../types';

export const dspmApi = {
  // R161-R163 — Wiz parity: executive summary + cross-border + NHI rotation
  // BE routes existed in dashboard_aggregations.py but had no FE wrapper.
  getExecutiveSummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/executive-summary'));
  },
  getCrossBorderAdequacyMap: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/cross-border/adequacy-map'));
  },
  getNHIRotationRecommendations: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/nhi/rotation-recommendations'));
  },

  // Data Discovery
  getDataAssets: async (params?: {
    provider?: CloudProvider;
    classification?: string;
    is_public?: boolean;
    limit?: number;
    page_size?: number;
    offset?: number;
  }): Promise<PaginatedResponse<DataAsset>> => {
    // Backend (/api/v1/data-assets) returns { data_assets, total, limit, offset }
    // and paginates via limit/offset — normalise to the PaginatedResponse shape
    // (items/total) the callers expect.
    const raw: any = _unwrap(await fetchClient.get<any>('/api/v1/dspm/data-assets', params as any));
    return {
      ...raw,
      items: raw?.items ?? raw?.data_assets ?? [],
      total: typeof raw?.total === 'number' ? raw.total : (raw?.data_assets?.length ?? 0),
    } as PaginatedResponse<DataAsset>;
  },

  getDataAsset: async (id: string): Promise<DataAsset> => {
    return _unwrap(await fetchClient.get<DataAsset>(`/api/v1/dspm/data-assets/${id}`));
  },

  triggerDiscovery: async (params?: { provider?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/dspm/data-assets/discover', params));
  },

  // Data Classification
  getClassificationRules: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/dspm/classification/rules'));
  },

  classifyAsset: async (assetId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/dspm/classification/${assetId}/classify`));
  },

  // Data Lineage
  getDataLineage: async (assetId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/dspm/lineage/${assetId}`));
  },

  getLineageFlows: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/lineage/flows'));
  },

  getLineageFlowRisks: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/lineage/flow-risks'));
  },

  getLineageStatistics: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/lineage/statistics'));
  },

  getLineageNodes: async (params?: { node_type?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/lineage/nodes', params as any));
  },

  getDataFlows: async (params?: { source?: string; destination?: string; encrypted_only?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/data-flows', params as any));
  },

  getRiskyFlows: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/risky-flows'));
  },

  // Data Access Monitoring — who DID access this asset.
  //
  // ⭐ THIS CALLED A ROUTE THAT REACHED A DIFFERENT SERVICE. The old path
  // `/api/v1/dspm/access/${assetId}/logs` is matched by the gateway's
  // longest-prefix rule `/api/v1/dspm/access` -> `/api/v1/access-requests`, so
  // the request went to the ACCESS REQUEST (approval workflow) router as
  // `/api/v1/access-requests/${assetId}/logs`, which serves no such route. The
  // "View Access Logs" button could never have rendered an access event, whatever
  // the table held. `/api/v1/dspm/access-log/query` is the canonical reader and
  // rewrites to itself.
  //
  // It returns an ENVELOPE, not a bare list: `coverage` says what this tier can
  // and cannot see, because `total: 0` alone reads as "nobody touched this data"
  // when it may mean the trail records no object-level access at all.
  getAccessLogs: async (
    assetId: string,
    params?: { hours?: number; limit?: number },
  ): Promise<{ logs: any[]; total: number; coverage?: any }> => {
    const raw: any = _unwrap(await fetchClient.get<any>(
      '/api/v1/dspm/access-log/query',
      { asset_id: assetId, ...(params || {}) } as any,
    ));
    return {
      logs: Array.isArray(raw?.logs) ? raw.logs : [],
      total: typeof raw?.total === 'number' ? raw.total : 0,
      coverage: raw?.coverage,
    };
  },

  getExposures: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/dspm/data-assets/exposed'));
  },

  getDashboard: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/data-assets/statistics'));
  },

  // Shadow Data Detection (port 8044)
  getShadowDataScans: async (params?: { status?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/dspm/shadow-data/scans', params as any));
  },

  getShadowDataFindings: async (params?: { severity?: string; type?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/dspm/shadow-data/findings', params as any));
  },

  getROTData: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/shadow-data/rot'));
  },

  getOrphanedSnapshots: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/dspm/shadow-data/orphaned-snapshots'));
  },

  triggerShadowDataScan: async (params?: { provider?: string }): Promise<any> => {
    // Wiz-parity: unified data-plane primitive. Gateway proxies to
    // dspm-aggregator /api/v1/scans (covers shadow-data + data-discovery).
    return _unwrap(await fetchClient.post<any>('/api/v1/data-plane/refresh/dspm', params || {}));
  },

  // Data Remediation (port 8045)
  getRemediationPlans: async (params?: { status?: string }): Promise<any[]> => {
    // BE data-remediation returns { plans: [...], total }. Extract the array.
    const res: any = _unwrap(await fetchClient.get<any>('/api/v1/dspm/remediation/plans', params as any));
    return Array.isArray(res) ? res : (res?.plans || res?.items || []);
  },

  getRemediationPlan: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/dspm/remediation/plans/${id}`));
  },

  createRemediationPlan: async (data: any): Promise<any> => {
    // BE has no manual-create endpoint — use /generate which auto-builds plans for an asset.
    return _unwrap(await fetchClient.post<any>('/api/v1/dspm/remediation/plans/generate', data));
  },

  generateRemediationPlans: async (data: { asset_id?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/dspm/remediation/plans/generate', data));
  },

  approveRemediationPlan: async (id: string): Promise<any> => {
    // BE data-remediation exposes PUT /api/v1/plans/{id}/approve (not POST).
    return _unwrap(await fetchClient.put<any>(`/api/v1/dspm/remediation/plans/${id}/approve`));
  },

  executeRemediationPlan: async (id: string, dryRun?: boolean): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/dspm/remediation/plans/${id}/execute`, { dry_run: dryRun }));
  },

  getRemediationTemplates: async (): Promise<any[]> => {
    // BE data-remediation returns { templates: [...], total }. Extract the array.
    const res: any = _unwrap(await fetchClient.get<any>('/api/v1/dspm/remediation/templates'));
    return Array.isArray(res) ? res : (res?.templates || res?.items || []);
  },

  // Data Sovereignty (port 8046)
  getSovereigntyPolicies: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/dspm/sovereignty/policies'));
  },

  createSovereigntyPolicy: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/dspm/sovereignty/policies', data));
  },

  getSovereigntyViolations: async (params?: { severity?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/dspm/sovereignty/violations', params as any));
  },

  getDataMap: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/sovereignty/data-map'));
  },

  getCrossBorderFlows: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/dspm/sovereignty/cross-border-flows'));
  },

  getDataResidency: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/sovereignty/residency'));
  },

  // SaaS Data Discovery — served by the saas-discovery child on the DSPM
  // umbrella (8007). Gateway rewrites /dspm/saas/* to the child's bare paths.
  getSaaSDataStores: async (params?: { connector_type?: string }): Promise<any[]> => {
    // BE returns { data_stores: [...], total }. Extract the array.
    const res: any = _unwrap(await fetchClient.get<any>('/api/v1/dspm/saas/data-stores', params as any));
    return Array.isArray(res) ? res : (res?.data_stores || res?.items || []);
  },

  getSaaSDataStore: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/dspm/saas/data-stores/${id}`));
  },

  getSnowflakeConnections: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/dspm/saas/snowflake/connections'));
  },

  getDatabricksWorkspaces: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/dspm/saas/databricks/workspaces'));
  },

  getMongoDBClusters: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/dspm/saas/mongodb/clusters'));
  },

  connectSaaSDataStore: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/dspm/saas/data-stores', data));
  },

  createSaaSDataStore: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/dspm/saas/data-stores', data));
  },

  updateSaaSDataStore: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/dspm/saas/data-stores/${id}`, data));
  },

  deleteSaaSDataStore: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete<any>(`/api/v1/dspm/saas/data-stores/${id}`));
  },

  getSaaSConnectors: async (): Promise<any[]> => {
    // BE saas-discovery serves the catalog at /api/v1/supported-platforms as
    // { platforms: [{ id, name, category, status, features }], ... }. Map its
    // real fields onto the connector shape the UI renders (no fabrication).
    const res: any = _unwrap(await fetchClient.get<any>('/api/v1/dspm/saas/connectors'));
    const list: any[] = Array.isArray(res) ? res : (res?.platforms || res?.connectors || res?.items || []);
    return list.map((p: any) => ({
      id: p.id,
      name: p.name || p.id,
      type: p.type || p.id,
      description: p.description || p.category || '',
      capabilities: p.capabilities || p.features || [],
      status: p.status || 'available',
      icon: p.icon || '',
    }));
  },

  getSaaSConnector: async (type: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/dspm/saas/connectors/${type}`));
  },

  testSaaSConnection: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/dspm/saas/test-connection', data));
  },

  syncSaaSDataStore: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/dspm/saas/data-stores/${id}/sync`));
  },

  // Data Risk Graph (port 8048)
  getDataRiskGraph: async (params?: { depth?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/risk-graph/graph', params as any));
  },

  getDataAttackPaths: async (params?: { min_risk_score?: number }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/dspm/risk-graph/attack-paths', params as any));
  },

  getToxicCombinations: async (params?: { limit?: number }): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/dspm/risk-graph/toxic-combinations', params as any));
  },

  getDataBlastRadius: async (assetId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/dspm/risk-graph/blast-radius/${assetId}`));
  },

  // Convenience aliases
  getAttackPaths: async (params?: { min_risk_score?: number }): Promise<any[]> => {
    return dspmApi.getDataAttackPaths(params);
  },

  getBlastRadius: async (assetId: string): Promise<any> => {
    return dspmApi.getDataBlastRadius(assetId);
  },

  // ========================================================================
  // NEW: AWS Scanner API (port 8040)
  // ========================================================================

  scanAWS: async (params: {
    provider: string;
    scan_type: string;
    region: string;
    save_to_database?: boolean;
    credentials?: any;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/data-discovery/scans/aws', params));
  },

  getScannerStatus: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/data-discovery/scans/status'));
  },

  // The scan is ASYNC: scanAWS returns {scan_id, status: "running"} at once and
  // the work continues server-side. A full pass takes minutes — measured — so a
  // synchronous POST died on the gateway's 60s timeout while the scan completed
  // and persisted, i.e. the UI reported a failure for work that succeeded.
  getScanResult: async (scanId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/data-discovery/scans/${encodeURIComponent(scanId)}`));
  },

  // ========================================================================
  // ========================================================================
  // NEW: Database-persisted Classifications (port 8007 aggregator)
  // ========================================================================

  getClassifications: async (params?: {
    tenant_id?: string;
    asset_id?: string;
    limit?: number;
  }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/classifications', params as any));
  },

  getExposuresList: async (params?: {
    tenant_id?: string;
    severity?: string;
    status?: string;
    limit?: number;
  }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/exposures', params as any));
  },

  // ========================================================================
  // Custom Classifiers API
  // ========================================================================

  getCustomClassifiers: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/dspm/classifiers'));
  },

  createCustomClassifier: async (data: {
    name: string;
    pattern: string;
    description: string;
    category: string;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/dspm/classifiers', data));
  },

  updateCustomClassifier: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/dspm/classifiers/${id}`, data));
  },

  deleteCustomClassifier: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete<any>(`/api/v1/dspm/classifiers/${id}`));
  },

  // ========================================================================
  // Data Subject Request (DSR) API
  // ========================================================================

  createDSR: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/dspm/dsr/requests', data));
  },

  listDSRs: async (status?: string): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/dspm/dsr/requests', status ? { status } : undefined));
  },

  getDSR: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/dspm/dsr/requests/${id}`));
  },

  executeDSR: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/dspm/dsr/requests/${id}/execute`));
  },

  getDSRDashboard: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/dsr/dashboard'));
  },

  // ========================================================================
  // SQL Lineage Extraction
  // ========================================================================

  extractSQLLineage: async (sql: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/dspm/lineage/extract-sql', { sql }));
  },

  // ========================================================================
  // Secure Connections
  // ========================================================================

  listConnectionsSecure: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/dspm/connections/secure'));
  },

  // ========================================================================
  // Query-Level Activity Monitoring
  // ========================================================================

  ingestQueryLog: async (data: { database_name: string; query_text: string; user_name: string; source_ip?: string; executed_at?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/dspm/query-monitor/ingest', data));
  },

  getRecentQueries: async (params?: { database_name?: string; user_name?: string; query_type?: string; classification?: string; limit?: number; offset?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/query-monitor/queries', params as any));
  },

  getSuspiciousQueries: async (params?: { database_name?: string; user_name?: string; min_risk_score?: number; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/query-monitor/queries/suspicious', params as any));
  },

  createQueryMonitorRule: async (data: { name: string; description?: string; condition?: any; severity?: string; is_active?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/dspm/query-monitor/rules', data));
  },

  getQueryMonitorRules: async (params?: { is_active?: boolean; severity?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/query-monitor/rules', params as any));
  },

  getQueryStats: async (params?: { database_name?: string; hours?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/query-monitor/stats', params as any));
  },

  buildQueryBaseline: async (data: { database_name: string; lookback_hours?: number }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/dspm/query-monitor/baseline', data));
  },

  getQueryAnomalies: async (params?: { database_name?: string; hours?: number; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/query-monitor/anomalies', params as any));
  },

  // ========================================================================
  // Data Masking / De-identification
  // ========================================================================

  createMaskingProfile: async (data: { name: string; description?: string; target_database?: string; field_rules?: Array<{ field_name: string; technique: string; options?: any }> }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/dspm/masking/profiles', data));
  },

  getMaskingProfiles: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/masking/profiles'));
  },

  getMaskingProfile: async (profileId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/dspm/masking/profiles/${profileId}`));
  },

  applyMasking: async (data: { profile_id: string; target_table?: string; where_clause?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/dspm/masking/apply', data));
  },

  applyMaskingDryRun: async (data: { profile_id: string; target_table?: string; where_clause?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/dspm/masking/apply/dry-run', data));
  },

  getMaskingHistory: async (params?: { profile_id?: string; status?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/masking/history', params as any));
  },

  getMaskingTechniques: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/dspm/masking/techniques'));
  },

  // Data lineage (parameterized)
  analyzeDataLineage: async (data: { account_id: string; days?: number }) =>
    _unwrap(await fetchClient.post<any>('/api/v1/dspm/data-lineage/analyze', data)),
  getDataLineageGraph: async () =>
    _unwrap(await fetchClient.get<any>('/api/v1/dspm/data-lineage/graph')),
  // Database content sampling
  sampleDatabaseContent: async (data: { resource_arn: string; provider: string }) =>
    _unwrap(await fetchClient.post<any>('/api/v1/dspm/data-sampling/database', data)),
  // Access request management
  getPendingAccessRequests: async () =>
    _unwrap(await fetchClient.get<any>('/api/v1/dspm/access/requests/pending')),
  approveAccessRequest: async (requestId: string, data: { approved_by: string; duration_hours?: number }) =>
    _unwrap(await fetchClient.post<any>(`/api/v1/dspm/access/requests/${requestId}/approve`, data)),
  denyAccessRequest: async (requestId: string, data: { denied_by: string; reason: string }) =>
    _unwrap(await fetchClient.post<any>(`/api/v1/dspm/access/requests/${requestId}/deny`, data)),
  getAccessAuditLog: async (params?: { requester?: string; status?: string }) =>
    _unwrap(await fetchClient.get<any>('/api/v1/dspm/access/audit-log', params as any)),
};

// ── Data Residency API ───────────────────────────────────────────────────────
export const dataResidencyApi = {
  getPolicies: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/admin/data-residency'));
  },
  createPolicy: async (policy: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/admin/data-residency', policy));
  },
  getViolations: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/admin/data-residency/violations'));
  },
};

// ── DSPM Content Scan API ────────────────────────────────────────────────────
export const dspmContentApi = {
    scanContent: async (content: string) => _unwrap(await fetchClient.post<any>('/api/v1/dspm/scan/content', { content })),
    scanS3Object: async (data: { bucket: string; key: string; region?: string }) => _unwrap(await fetchClient.post<any>('/api/v1/dspm/scan/s3-object', data)),
    scanS3Bucket: async (data: { bucket: string; region?: string }) => _unwrap(await fetchClient.post<any>('/api/v1/dspm/scan/s3-bucket-sample', data)),
    getResults: async () => _unwrap(await fetchClient.get<any>('/api/v1/dspm/scan/results')),
    getResult: async (id: string) => _unwrap(await fetchClient.get<any>(`/api/v1/dspm/scan/results/${id}`)),
    getClassifications: async () => _unwrap(await fetchClient.get<any>('/api/v1/dspm/classifications')),
    addCustomPattern: async (data: any) => _unwrap(await fetchClient.post<any>('/api/v1/dspm/classifications/custom', data)),
};

// ── AI Training Data API ─────────────────────────────────────────────────────
export const aiTrainingDataApi = {
  // Scans
  getScans: async (params?: { status?: string; page?: number; page_size?: number }): Promise<any> =>
    _unwrap(await fetchClient.get<any>('/api/v1/dspm/ai-training/scans', params as any)),
  triggerScan: async (data: { scope: string; data_sources?: string[]; classification_rules?: string[] }): Promise<any> =>
    _unwrap(await fetchClient.post<any>('/api/v1/dspm/ai-training/scan', data)),
  getScanDetail: async (scanId: string): Promise<any> =>
    _unwrap(await fetchClient.get<any>(`/api/v1/dspm/ai-training/scan/${scanId}`)),

  // Datasets
  getDatasets: async (params?: { risk_level?: string; classification?: string; page?: number; page_size?: number }): Promise<any> =>
    _unwrap(await fetchClient.get<any>('/api/v1/dspm/ai-training/datasets', params as any)),
  getDatasetDetail: async (datasetId: string): Promise<any> =>
    _unwrap(await fetchClient.get<any>(`/api/v1/dspm/ai-training/datasets/${datasetId}`)),

  // Risk & Classification
  getRisks: async (params?: { dataset_id?: string; severity?: string }): Promise<any> =>
    _unwrap(await fetchClient.get<any>('/api/v1/dspm/ai-training/risks', params as any)),
  classify: async (data: { dataset_id: string; rules?: string[] }): Promise<any> =>
    _unwrap(await fetchClient.post<any>('/api/v1/dspm/ai-training/classify', data)),

  // Compliance & Stats
  getCompliance: async (params?: { framework?: string }): Promise<any> =>
    _unwrap(await fetchClient.get<any>('/api/v1/dspm/ai-training/compliance', params as any)),
  getStats: async (): Promise<any> =>
    _unwrap(await fetchClient.get<any>('/api/v1/dspm/ai-training/stats')),
};
